/**
 * Shared Chat Rendering Components
 * ChartRenderer, parseContentWithCharts, FormattedChatMessage
 * Used by ClientFileChatSidebar and Manager PA VoicePage.
 */

import { useMemo } from 'react';
import ReactMarkdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  PieChart, Pie, Cell, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

const CHART_COLORS = ['#3b82f6', '#ef4444', '#22c55e', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316', '#14b8a6', '#6366f1'];

function formatChartValue(value: number) {
  if (Math.abs(value) >= 100) return `$${value.toLocaleString()}`;
  return value.toLocaleString();
}

export function ChartRenderer({ chartData }: { chartData: { type: string; title?: string; data: any[]; keys?: string[] } }) {
  const { type, title, data, keys } = chartData;
  if (!data || data.length === 0) return null;

  const normalizedType = type.toLowerCase().replace(/[_\s-]/g, '');
  const chartHeight = normalizedType === 'horizontalbar' || normalizedType === 'horizontal' || normalizedType === 'hbar' ? Math.max(250, data.length * 30) : 280;
  // Auto-detect the X-axis / label key. LLMs frequently produce `year`,
  // `month`, `period`, `category` instead of the canonical `name`.
  const nameKey = detectNameKey(data);

  const renderChart = () => {
    switch (normalizedType) {
      case 'pie': case 'piechart': case 'donut': case 'donutchart':
        return (
          <PieChart>
            <Pie data={data} dataKey="value" nameKey={nameKey} cx="35%" cy="50%"
              outerRadius={90} innerRadius={normalizedType.includes('donut') ? 50 : 0}>
              {data.map((_: any, i: number) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
            </Pie>
            <Tooltip formatter={(v: number) => formatChartValue(v)} />
            <Legend layout="vertical" align="right" verticalAlign="middle" iconType="square" wrapperStyle={{ fontSize: '11px' }} />
          </PieChart>
        );

      case 'bar': case 'barchart':
        return (
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis dataKey={nameKey} tick={{ fontSize: 10 }} angle={data.length > 6 ? -30 : 0} textAnchor={data.length > 6 ? 'end' : 'middle'} height={data.length > 6 ? 60 : 30} />
            <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`} />
            <Tooltip formatter={(v: number) => formatChartValue(v)} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {keys ? keys.map((key, i) => <Bar key={key} dataKey={key} fill={CHART_COLORS[i % CHART_COLORS.length]} radius={[4, 4, 0, 0]} activeBar={false}/>) : <Bar dataKey="value" fill={CHART_COLORS[0]} radius={[4, 4, 0, 0]} activeBar={false}>
              {data.map((_: any, i: number) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
            </Bar>}
          </BarChart>
        );

      case 'horizontalbar': case 'horizontal': case 'hbar':
        return (
          <BarChart data={data} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={(v) => v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`} />
            <YAxis type="category" dataKey={nameKey} tick={{ fontSize: 10 }} width={120} />
            <Tooltip formatter={(v: number) => formatChartValue(v)} />
            {keys ? keys.map((key, i) => <Bar key={key} dataKey={key} fill={CHART_COLORS[i % CHART_COLORS.length]} activeBar={false}/>) : <Bar dataKey="value" fill={CHART_COLORS[0]} radius={[0, 4, 4, 0]} activeBar={false}/>}
          </BarChart>
        );

      case 'line': case 'linechart':
        return (
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis dataKey={nameKey} tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`} />
            <Tooltip formatter={(v: number) => formatChartValue(v)} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {keys ? keys.map((key, i) => <Line key={key} type="monotone" dataKey={key} stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2} dot={{ r: 4 }} />) : <Line type="monotone" dataKey="value" stroke={CHART_COLORS[0]} strokeWidth={2} dot={{ r: 4 }} />}
          </LineChart>
        );

      default:
        return (
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis dataKey={nameKey} tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} />
            <Tooltip formatter={(v: number) => formatChartValue(v)} />
            <Bar dataKey="value" fill={CHART_COLORS[0]} radius={[4, 4, 0, 0]} activeBar={false}>
              {data.map((_: any, i: number) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
            </Bar>
          </BarChart>
        );
    }
  };

  return (
    <div className="my-3 p-3 bg-black/10 dark:bg-white/5 rounded-lg border border-white/10 overflow-hidden">
      {title && <h4 className="text-sm font-semibold mb-2 text-center">{title}</h4>}
      <ResponsiveContainer width="99%" height={chartHeight}>
        {renderChart()}
      </ResponsiveContainer>
    </div>
  );
}

export function parseContentWithCharts(content: string): Array<{ type: 'text' | 'chart'; content: string; chartData?: any }> {
  const parts: Array<{ type: 'text' | 'chart'; content: string; chartData?: any }> = [];

  // We accept both the canonical form `[CHART] {…} [/CHART]` and the
  // common LLM mistake of dropping the closing tag. Strategy:
  //   1. Find each `[CHART]` opener.
  //   2. Try to find a matching `[/CHART]` closer; if present, use the
  //      body between them.
  //   3. Otherwise, scan from `[CHART]` for a balanced JSON object - read
  //      until the brace count returns to zero - and use THAT as the
  //      body. The text after the JSON object resumes as normal markdown.
  const openTag = /(?:```(?:json)?\s*)?\[CHART\]/g;
  let lastIndex = 0;
  let m: RegExpExecArray | null;

  while ((m = openTag.exec(content)) !== null) {
    // Flush text before this chart.
    if (m.index > lastIndex) {
      parts.push({ type: 'text', content: content.slice(lastIndex, m.index) });
    }
    const afterOpen = openTag.lastIndex;

    // Look for the closing tag from the position after `[CHART]`.
    const closeIdx = content.indexOf('[/CHART]', afterOpen);
    let chartBody: string | null = null;
    let consumedTo = afterOpen;

    if (closeIdx !== -1) {
      chartBody = content.slice(afterOpen, closeIdx).trim();
      consumedTo = closeIdx + '[/CHART]'.length;
      // Eat an optional trailing ``` fence.
      const tail = content.slice(consumedTo).match(/^\s*```/);
      if (tail) consumedTo += tail[0].length;
    } else {
      // No closing tag - scan for a balanced JSON object.
      const tail = content.slice(afterOpen);
      const firstBrace = tail.search(/\{|\[/);
      if (firstBrace !== -1) {
        let depth = 0;
        let i = firstBrace;
        let inString = false;
        let escape = false;
        const opener = tail[i];
        const closer = opener === '{' ? '}' : ']';
        for (; i < tail.length; i++) {
          const ch = tail[i];
          if (escape) { escape = false; continue; }
          if (ch === '\\') { escape = true; continue; }
          if (ch === '"') { inString = !inString; continue; }
          if (inString) continue;
          if (ch === opener) depth++;
          else if (ch === closer) {
            depth--;
            if (depth === 0) { i++; break; }
          }
        }
        chartBody = tail.slice(firstBrace, i).trim();
        consumedTo = afterOpen + i;
      }
    }

    if (chartBody) {
      try {
        const chartData = JSON.parse(chartBody);
        parts.push({ type: 'chart', content: '', chartData });
      } catch {
        // Couldn't parse - render the original chunk as text so the user
        // sees something rather than nothing.
        parts.push({ type: 'text', content: content.slice(m.index, consumedTo) });
      }
    } else {
      parts.push({ type: 'text', content: content.slice(m.index, afterOpen) });
    }

    lastIndex = consumedTo;
    openTag.lastIndex = consumedTo;
  }

  if (lastIndex < content.length) {
    parts.push({ type: 'text', content: content.slice(lastIndex) });
  }

  return parts.length > 0 ? parts : [{ type: 'text', content }];
}

/**
 * Picks the best X-axis key from the first data point. Used by chart
 * renderers when the LLM produces data like `{year: "FY24", revenue: 100}`
 * instead of the canonical `{name: "FY24", value: 100}` - without this,
 * the X-axis would be blank.
 */
export function detectNameKey(data: any[]): string {
  if (!data || data.length === 0) return 'name';
  const first = data[0];
  if (!first || typeof first !== 'object') return 'name';
  const preferred = ['name', 'label', 'category', 'year', 'fy', 'month', 'period', 'date', 'quarter'];
  for (const k of preferred) {
    if (k in first) return k;
  }
  // Fall back to the first string field.
  for (const k of Object.keys(first)) {
    if (typeof first[k] === 'string') return k;
  }
  return 'name';
}

/**
 * Renders a chat message with markdown, tables, and charts.
 * Auto-adapts to the current theme via Tailwind dark: variants - no need
 * for callers to pass darkTheme; the nearest `.dark` ancestor on the html
 * element controls light/dark colors.
 *
 * The `darkTheme` prop is kept for backwards compat (Manager PA passes it),
 * but the default styling already does the right thing in both modes.
 */
export function FormattedChatMessage({ content, darkTheme }: { content: string; darkTheme?: boolean }) {
  // When darkTheme is explicitly true (Manager PA's voice-on-black panel),
  // force-paint the dark variant by wrapping in a .dark scope. Otherwise let
  // the ambient theme decide via standard Tailwind dark: variants.
  const components: Components = useMemo(() => ({
    table: ({ children, ...props }) => (
      <div className="my-2 overflow-x-auto max-w-full not-prose">
        <table {...props} className="text-xs w-full border-collapse min-w-0 border border-zinc-300 dark:border-zinc-700">{children}</table>
      </div>
    ),
    th: ({ children, ...props }) => (
      <th {...props} className="text-left p-2 font-semibold bg-zinc-100 dark:bg-zinc-800 text-foreground text-xs border border-zinc-300 dark:border-zinc-700">{children}</th>
    ),
    td: ({ children, ...props }) => (
      <td {...props} className="p-2 text-xs align-top text-foreground border border-zinc-200 dark:border-zinc-700">{children}</td>
    ),
    pre: ({ children, ...props }) => (
      <pre {...props} className="my-2 p-3 bg-zinc-100 dark:bg-zinc-900 rounded-lg overflow-x-auto text-xs text-foreground">{children}</pre>
    ),
    code: ({ children, className, ...props }) => {
      if (className?.includes('language-')) return <code className={className} {...props}>{children}</code>;
      return <code className="text-primary bg-primary/10 dark:text-[#7c75f0] dark:bg-[#5048e5]/10 px-1 py-0.5 rounded text-xs" {...props}>{children}</code>;
    },
    strong: ({ children }) => <strong className="font-semibold text-foreground">{children}</strong>,
    a: ({ children, href }) => <a href={href} target="_blank" rel="noopener" className="text-primary dark:text-[#7c75f0] hover:underline">{children}</a>,
    p: ({ children, ...props }) => <p {...props} className="text-foreground">{children}</p>,
    li: ({ children, ...props }) => <li {...props} className="text-foreground">{children}</li>,
    h1: ({ children, ...props }) => <h1 {...props} className="text-foreground font-bold">{children}</h1>,
    h2: ({ children, ...props }) => <h2 {...props} className="text-foreground font-bold">{children}</h2>,
    h3: ({ children, ...props }) => <h3 {...props} className="text-foreground font-bold">{children}</h3>,
    h4: ({ children, ...props }) => <h4 {...props} className="text-foreground font-semibold">{children}</h4>,
  }), []);

  const safeContent = typeof content === 'string' ? content : JSON.stringify(content || '');
  const parts = useMemo(() => parseContentWithCharts(safeContent), [safeContent]);

  // Wrap in `.dark` for explicit dark mode callers (Manager PA). Otherwise
  // we live inside whatever theme the page already established.
  const rootClass = darkTheme === true ? 'dark overflow-hidden max-w-full' : 'overflow-hidden max-w-full';

  return (
    <div className={rootClass}>
      {parts.map((part, i) =>
        part.type === 'chart' && part.chartData ? (
          <ChartRenderer key={i} chartData={part.chartData} />
        ) : (
          <div key={i} className="text-sm prose prose-sm dark:prose-invert max-w-none prose-p:my-1.5 prose-ul:my-1 prose-ol:my-1 overflow-hidden break-words text-foreground">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
              {part.content}
            </ReactMarkdown>
          </div>
        )
      )}
    </div>
  );
}
